"use client";

import { useLocale, useTranslations } from "next-intl";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import {
  Mail,
  Lock,
  User,
  Phone,
  Upload,
  EyeOff,
  Eye,
  CheckCircle2,
  LogIn,
} from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { useGetUser, useRegister } from "@/hooks/main-hook";
import Loading from "./Loading";
import Image from "next/image";
import { roles } from "@/data/main-data";
import { useRouter } from "next/navigation";

export default function Register() {
  const locale = useLocale();
  const t = useTranslations("Auth");
  const [fileName, setFileName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [selectedRole, setSelectedRole] = useState("developer");

  const { mutate, isPending } = useRegister();
  const isRtl = locale === "ar";

  const { data } = useGetUser();
  const user = data?.user;
  const navigate = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  }: any = useForm({
    mode: "all",
    defaultValues: { role: "developer" },
  });

  const handleRegister = (data: any) => {
    const { file, ...res } = data;
    if (file[0].size > 20 * 1024 * 1024) {
      toast.info(
        isRtl
          ? "حجم الملف يجب أن يكون أقل من 20 ميجابايت"
          : "File size must be less than 20MB.",
      );
      return;
    }

    mutate(
      { ...res, file: file[0], type: "file" },
      {
        onSuccess: () => {
          reset();
          setFileName("");
          setSelectedRole("developer");
          setAgree(false);
        },
      },
    );
  };

  useEffect(() => {
    if (user?.role === "bank") {
      navigate.push(`/${locale}/bank`);
      return;
    }

    if (user && user?.role !== "bank") {
      navigate.push(`/${locale}/dashboard`);
    }
  }, [user, navigate, locale]);

  return (
    <main
      dir={isRtl ? "rtl" : "ltr"}
      className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[#050505] text-[#EDE5D6] overflow-hidden font-cairo"
    >
      <section className="hidden lg:flex lg:col-span-6 relative overflow-hidden group">
        <motion.div
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 z-0"
        >
          <Image
            src="/auth.png"
            alt="Saudi Skyline Vision 2030"
            fill
            className="opacity-100 transition-all duration-1000"
          />
        </motion.div>
        {/* Overlays */}
        <div
          className={`absolute inset-0 bg-gradient-to-${isRtl ? "left" : "right"} from-[#050505] via-[#050505]/40 to-transparent z-10`}
        />

        <div className="relative z-20 flex flex-col justify-between p-16 w-full h-full">
          <div className="text-3xl font-black text-white tracking-tighter uppercase font-sans">
            SNA<span className="text-[#C8A96E]">.</span>
          </div>
        </div>
      </section>

      <section className="lg:col-span-6 flex flex-col items-center justify-start p-6 md:p-12 overflow-y-auto bg-[#050505] relative custom-scrollbar">
        {/* Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C8A96E]/5 blur-[150px] rounded-full pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, x: isRtl ? -30 : 30 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-2xl relative z-10 py-10"
        >
          <div className="mb-10 text-center lg:text-start">
            <h1 className="text-4xl font-black text-white mb-2 tracking-tight">
              {t("welcome")} <span className="text-[#C8A96E]">.</span>
            </h1>
            <p className="text-gray-500">{t("signupMsg")}</p>
          </div>

          <form onSubmit={handleSubmit(handleRegister)} className="space-y-8">
            <div className="space-y-4">
              <label className="text-xs font-bold text-[#C8A96E] uppercase tracking-[0.2em] px-1 font-cairo">
                {isRtl ? "نوع الشراكة" : "Partnership Type"}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {roles.map((role) => (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => {
                      setSelectedRole(role.value);
                      setValue("role", role.value);
                    }}
                    className={`relative flex flex-col p-5 rounded-2xl border transition-all duration-300 text-start group ${
                      selectedRole === role.value
                        ? "bg-[#C8A96E]/10 border-[#C8A96E] shadow-[0_0_20px_rgba(200,169,110,0.1)]"
                        : "bg-[#11141D]  hover:border-gray-600"
                    }`}
                  >
                    {selectedRole === role.value && (
                      <CheckCircle2
                        className={`absolute top-1/2 -translate-y-1/2 ${isRtl ? "left-4" : "right-4"} text-[#C8A96E]`}
                        size={18}
                      />
                    )}
                    <span
                      className={`text-sm font-bold mb-1 ${selectedRole === role.value ? "text-[#C8A96E]" : "text-white"}`}
                    >
                      {isRtl ? role.labelAr : role.labelEn}
                    </span>
                  </button>
                ))}
              </div>
              <input type="hidden" {...register("role", { required: true })} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Username */}
              <div className="space-y-2 relative group">
                <User
                  className={`absolute bottom-4 ${isRtl ? "right-4" : "left-4"} text-gray-500 group-focus-within:text-[#C8A96E] transition-colors`}
                  size={18}
                />
                <input
                  {...register("username", { required: t("required") })}
                  className="w-full bg-[#11141D] border border-[#2D3340] rounded-xl py-4 ps-12 pe-4 text-white focus:outline-none focus:border-[#C8A96E]/50 transition-all placeholder:text-gray-700"
                  placeholder={t("username")}
                />
              </div>

              {/* Email */}
              <div className="space-y-2 relative group">
                <Mail
                  className={`absolute bottom-4 ${isRtl ? "right-4" : "left-4"} text-gray-500 group-focus-within:text-[#C8A96E] transition-colors`}
                  size={18}
                />
                <input
                  {...register("email", {
                    required: t("required"),
                    pattern: /^\S+@\S+$/i,
                  })}
                  className="w-full bg-[#11141D] border border-[#2D3340] rounded-xl py-4 ps-12 pe-4 text-white focus:outline-none focus:border-[#C8A96E]/50 transition-all placeholder:text-gray-700"
                  placeholder={t("email")}
                />
              </div>

              {/* Phone — country picker with flags, Saudi Arabia by default */}
              <div className="space-y-2 relative group">
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: t("required") }}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      international
                      defaultCountry="SA"
                      countryCallingCodeEditable={false}
                      placeholder="5X XXX XXXX"
                      className="sna-phone-dark"
                    />
                  )}
                />
                {errors?.phone && (
                  <p className="text-xs text-red-400">{String(errors.phone.message)}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2 relative group">
                <Lock
                  className={`absolute bottom-4 ${isRtl ? "right-4" : "left-4"} text-gray-500 group-focus-within:text-[#C8A96E] transition-colors`}
                  size={18}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: t("required"),
                    minLength: 6,
                  })}
                  className="w-full bg-[#11141D] border border-[#2D3340] rounded-xl py-4 ps-12 pe-12 text-white focus:outline-none focus:border-[#C8A96E]/50 transition-all placeholder:text-gray-700"
                  placeholder={t("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 ${isRtl ? "left-4" : "right-4"} text-gray-600 hover:text-white transition-colors`}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold text-[#C8A96E] uppercase tracking-[0.2em] px-1">
                {isRtl ? "المستندات القانونية" : "Legal Documents"}
              </label>
              <label
                className={`flex flex-col items-center justify-center w-full h-32 rounded-2xl border-2 border-dashed transition-all cursor-pointer ${
                  errors?.file
                    ? "border-red-500/50 bg-red-500/5"
                    : "border-[#2D3340] hover:border-[#C8A96E]/50 bg-[#11141D]"
                }`}
              >
                <input
                  type="file"
                  // choose only pdf, doc, docx
                  accept=".zip"
                  className="hidden"
                  {...register("file", {
                    required: t("required"),
                    onChange: (e: any) =>
                      setFileName(e.target.files?.[0]?.name || ""),
                  })}
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="p-3 rounded-full bg-[#C8A96E]/10 text-[#C8A96E]">
                    <Upload size={24} />
                  </div>
                  <span className="text-sm font-medium text-white">
                    {fileName || t("file")}
                  </span>
                  <span className="text-[10px] text-gray-500">
                    {t("fileNotes")}
                  </span>
                </div>
              </label>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 p-4 bg-[#11141D] rounded-xl border border-[#2D3340]">
                <input
                  type="checkbox"
                  id="agree"
                  checked={agree}
                  onChange={() => setAgree(!agree)}
                  className="w-5 h-5 accent-[#C8A96E] rounded border-gray-700 bg-gray-900 cursor-pointer"
                />
                <label
                  htmlFor="agree"
                  className="text-sm text-gray-400 leading-tight"
                >
                  {isRtl ? "أوافق على" : "I agree to"}{" "}
                  <Link
                    href="/terms"
                    className="text-[#C8A96E] hover:underline font-bold mx-1"
                  >
                    {isRtl ? "الشروط والأحكام" : "Terms & Conditions"}
                  </Link>{" "}
                  {isRtl ? "الخاصة بالمنصة" : ""}
                </label>
              </div>

              {isPending ? (
                <div className="flex justify-center p-4 bg-[#111] rounded-xl">
                  <Loading />
                </div>
              ) : (
                <motion.button
                  type="submit"
                  disabled={!agree}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#C8A96E] hover:bg-[#D9B882] disabled:opacity-30 disabled:grayscale text-black font-black py-5 rounded-xl shadow-xl shadow-[#C8A96E]/10 flex items-center justify-center gap-3 uppercase tracking-widest transition-all"
                >
                  <LogIn size={20} />
                  {t("signup")}
                </motion.button>
              )}
            </div>
          </form>

          <div className="mt-10 text-center text-gray-500">
            {t("haveAccount")}{" "}
            <Link
              href="/auth/login"
              className="text-white font-bold hover:text-[#C8A96E] transition-colors border-b border-white/10 ml-1"
            >
              {t("login")}
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
